import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Receipt } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import {
  type OpportunityListResponse,
  type ClientListResponse,
  type InvoiceDetail,
  type InvoiceSeriesDto,
} from '@toga/shared'

export default function NewInvoicePage() {
  const { t } = useTranslation('billing')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [series, setSeries] = useState<InvoiceSeriesDto[]>([])
  const [seriesId, setSeriesId] = useState('')
  const [clients, setClients] = useState<ClientListResponse['items']>([])
  const [clientId, setClientId] = useState(searchParams.get('clientId') ?? '')
  const [cases, setCases] = useState<OpportunityListResponse['items']>([])
  const [opportunityId, setCaseId] = useState(searchParams.get('opportunityId') ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.get<{ items: InvoiceSeriesDto[] }>('/invoice-series').then((r) => {
      setSeries(r.items)
      const def = r.items.find((s) => s.isDefault) ?? r.items[0]
      if (def) setSeriesId(def.id)
    })
  }, [])

  const loadClients = useCallback(async (q: string) => {
    // Ordenado por nombre cuando no hay búsqueda (con `q` el servidor ordena
    // por relevancia, que es lo que se espera al teclear).
    const params = new URLSearchParams({ pageSize: '30', sortBy: 'name', sortDir: 'asc' })
    if (q.trim()) params.set('q', q.trim())
    const resp = await api.get<ClientListResponse>(`/clients?${params.toString()}`)
    setClients(resp.items)
  }, [])

  useEffect(() => {
    void loadClients('')
  }, [loadClients])

  useEffect(() => {
    if (!clientId) {
      setCases([])
      return
    }
    api
      .get<OpportunityListResponse>(`/opportunities?clientId=${clientId}&pageSize=30`)
      .then((r) => setCases(r.items))
      .catch(() => setCases([]))
  }, [clientId])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!seriesId || !clientId) {
      setError(t('new.errors.missingFields'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const { invoice } = await api.post<{ invoice: InvoiceDetail }>('/invoices', {
        seriesId,
        clientId,
        opportunityId: opportunityId || null,
        lines: [],
      })
      navigate(`/billing/${invoice.id}`, { replace: true })
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('new.errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4 animate-slideUp">
      <header>
        <Link to="/billing" className="text-xs t-faint hover:underline inline-flex items-center gap-1">
          <ArrowLeft size={12} /> {t('new.backLink')}
        </Link>
        <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2 mt-1">
          <Receipt size={18} className="text-toga-700 dark:text-toga-300" />
          {t('new.title')}
        </h1>
      </header>

      <form onSubmit={onSubmit} className="surface p-5 max-w-xl space-y-4">
        <label className="block">
          <span className="text-xs t-muted">{t('new.fields.series')}</span>
          <select
            value={seriesId}
            onChange={(e) => setSeriesId(e.target.value)}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
            required
          >
            {series.length === 0 && <option value="">{t('new.series.empty')}</option>}
            {series.map((s) => (
              <option key={s.id} value={s.id}>
                {s.prefix} · {s.name} — {s.issuerName}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs t-muted">{t('new.fields.client')}</span>
          <input
            placeholder={t('new.fields.clientSearchPlaceholder')}
            onChange={(e) => void loadClients(e.target.value)}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5 mb-2"
          />
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary"
            required
            size={6}
          >
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
              </option>
            ))}
          </select>
        </label>

        {cases.length > 0 && (
          <label className="block">
            <span className="text-xs t-muted">{t('new.fields.opportunity')}</span>
            <select
              value={opportunityId}
              onChange={(e) => setCaseId(e.target.value)}
              className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
            >
              <option value="">{t('new.opportunity.none')}</option>
              {cases.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} · {c.title}
                </option>
              ))}
            </select>
          </label>
        )}

        {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={busy}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {t('new.submit')}
          </button>
        </div>
      </form>
    </div>
  )
}
